import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import { Checkbox, FormControlLabel } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

import checkSvg from 'assets/img/check-circle.svg';
import { getUserType } from 'redux/slices/userSlice';
import { Tooltip } from 'shared/hooks/use-tooltips-for';
import { Exam, ExamStatus, ICL, TaskKeysType, TestTask, WorkType } from 'shared/types';

import StudentTaskWrapper from '../StudentTaskWrapper';

interface StudentTestTaskProps extends TestTask {
  icl: ICL | Exam | null;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: any) => void;
  saveTaskAnswer: (id: string) => Promise<void>;
  hw?: boolean;
  preview?: boolean;
  studentName?: string;
  index: number;
  points: number;
  workTaskType?: WorkType;
}

export default function StudentTestTask({
  icl,
  text,
  subText,
  answer,
  hasAnswer,
  hasStar,
  checkedAt,
  completedAt,
  handleChangeTaskByKey,
  id,
  hasPhoto,
  answersPhotos,
  saveTaskAnswer,
  deadline,
  audioUrl,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  testItems,
  maxGrade,
  studentName,
  index,
  points,
  workTaskType,
  teacherPoints,
}: StudentTestTaskProps) {
  const userType = useSelector(getUserType);

  const isExam = icl?.type === WorkType.exam;
  const isExamCompleted = isExam && icl?.status === ExamStatus.readyToCheck;
  const testItemsWithCorrectAnswers = testItems.filter(item => item.isRight);
  const areStudentAnswersCorrect = testItemsWithCorrectAnswers.every(item => item.answer);

  const changeFormikTestItems = (testItemId: string) => {
    const changedTestItems = testItems.map(item => (item.id === testItemId ? { ...item, answer: !item.answer } : item));

    handleChangeTaskByKey(id, 'testItems', changedTestItems);
  };

  const isStudent = userType === 'Student';
  const areStudentAnswers = testItems.some(testItem => testItem.answer);
  const isDisabledSubmitButton = !areStudentAnswers || !!checkedAt || !!completedAt;
  const isCheckboxDisabled = !!checkedAt || !!completedAt || !isStudent || !!preview;

  const renderTeacherChoices = (isRight: boolean) => {
    if (!isStudent && isRight)
      return (
        <Tooltip title="wordChoiseTeacher">
          <img src={checkSvg} alt="check" />
        </Tooltip>
      );
  };

  return (
    <StudentTaskWrapper
      icl={icl}
      taskText={text}
      answersPhotos={answersPhotos}
      id={id}
      handleChangeTaskByKey={handleChangeTaskByKey}
      checkedAt={checkedAt}
      hasPhoto={hasPhoto}
      completedAt={completedAt}
      saveTaskAnswer={saveTaskAnswer}
      deadline={deadline}
      audioUrl={audioUrl}
      answer={answer}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      videoUrl={videoUrl}
      taskPhotos={taskPhotos}
      editedAnswerPhotos={editedAnswerPhotos}
      hw={hw}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      preview={preview}
      subText={subText}
      isDisabledSubmitButton={isDisabledSubmitButton}
      isSubmitButton
      taskName="test"
      areStudentAnswersCorrect={areStudentAnswersCorrect}
      isExamCompleted={isExamCompleted}
      grade={isExamCompleted && areStudentAnswersCorrect ? maxGrade : 0}
      maxGrade={maxGrade}
      studentName={studentName}
      index={index}
      points={points}
      workTaskType={workTaskType}
      teacherPoints={teacherPoints}
    >
      <ul className="flex flex-col gap-3 py-[20px] lg:gap-4 lg:py-8">
        {testItems.map(testItem => (
          <li key={testItem.id} className="flex items-center">
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isCheckboxDisabled}
                  checked={testItem.answer}
                  checkedIcon={
                    isStudent || testItem.isRight === testItem.answer ? <CheckBoxIcon /> : <DisabledByDefaultIcon />
                  }
                  sx={
                    isStudent || testItem.isRight === testItem.answer
                      ? {
                          color: '#08B781',
                          '&.Mui-checked': {
                            color: '#08B781',
                          },
                        }
                      : {
                          color: '#b70808',
                          '&.Mui-checked': {
                            color: '#b70808',
                          },
                        }
                  }
                  onChange={() => changeFormikTestItems(testItem.id)}
                />
              }
              label={
                <div className="flex gap-[8px]">
                  {testItem.text} {renderTeacherChoices(testItem.isRight ?? false)}
                </div>
              }
              sx={{
                marginRight: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#242C2C',
                  fontFamily: 'Mulish',
                  lineHeight: '24.5px',
                  '@media (min-width: 1024px)': {
                    fontSize: 20,
                  },
                },
              }}
            />
          </li>
        ))}
      </ul>
    </StudentTaskWrapper>
  );
}
